<template>
  <li class="member-indoor-item-building-node">
    <div
      v-if="isTarget"
      class="node-contents"
      v-bind:class="{ selected: isSelected }"
      v-bind:style="{ '--padding-left-add': `${indent * 48}px` }"
      v-on:click="$_onItemClick"
    >
      <!-- ラベル -->
      <div class="item-label">
        <div class="arrow-wrapper">
          <arrow-down
            v-if="indoorLevelNodes.length"
            class="arrow"
            v-bind:class="{ 'is-open': isChildNodeShown }"
            v-on:click.stop="$_onArrowClick"
          />
        </div>
        <div class="icon-wrapper">
          <building-icon class="building-icon" />
        </div>
        <div class="item-name" v-on:click.stop="$_onItemNameClick">
          {{ name }}
        </div>
      </div>
      <!-- ユーザー数 -->
      <div class="counts">
        <template v-if="showUsersCount">
          <b-row no-gutters class="users-count">
            <user-icon class="user-icon" />
            <span>
              {{ usersCount }}
              {{ $t('appMemberIndoorItemBuildingNode.case') }}
            </span>
          </b-row>
          <!-- タグユーザー -->
          <b-row
            v-if="$store.getters['user/hasTagUser']"
            no-gutters
            class="users-count"
          >
            <tag-user-icon class="user-icon" />
            <span>
              {{ tagUsersCount }}
              {{ $t('appMemberIndoorItemSiteNode.case') }}
            </span>
          </b-row>
        </template>
      </div>
    </div>
    <ul v-if="indoorLevelNodes.length" class="children-list">
      <member-indoor-item-level-node
        v-for="indoorLevelNode in indoorLevelNodes"
        v-bind:key="`indoor-level-${indoorLevelNode.id}`"
        v-show="isChildNodeShown"
        v-bind="indoorLevelNode"
        v-bind:users="users"
        v-bind:selectedId="selectedId"
        v-bind:indent="indent + 1"
        v-bind:show-tag-user-count="showTagUsersCount"
        v-on:indoor-item-selected="$_onChildItemSelected"
      />
    </ul>
  </li>
</template>

<script>
import MemberIndoorItemLevelNode from '@/components/member/AppMemberIndoorItemLevelNode.vue'
import ArrowDown from '@/assets/ifield/icon_arrow_down.svg?component'
import BuildingIcon from '@/assets/ifield/ic_building.svg?component'
import UserIcon from '@/assets/ifield/ic_user.svg?component'
import TagUserIcon from '@/assets/ifield/ic_tag_user.svg?component'
import { TAG_USER_ROLE_ID } from 'ifield-core-components'

export default {
  name: 'AppMemberIndoorItemBuildingNode',
  components: {
    'member-indoor-item-level-node': MemberIndoorItemLevelNode,
    'arrow-down': ArrowDown,
    'building-icon': BuildingIcon,
    'user-icon': UserIcon,
    TagUserIcon,
  },
  inheritAttrs: false,
  props: {
    /**
     * ID
     */
    id: {
      type: Number,
      default: null,
    },
    /**
     * 名称
     */
    name: {
      type: String,
      default: '',
    },
    /**
     * 屋内レベルノード
     */
    indoorLevelNodes: {
      type: Array,
      default: () => [],
    },
    /**
     * 検索ターゲットフラグ
     */
    isTarget: {
      type: Boolean,
      default: false,
    },
    /**
     * ユーザー情報
     */
    users: {
      type: Array,
      default: () => [],
    },
    /**
     * 選択されているID
     */
    selectedId: {
      type: Object,
      default: () => {},
    },
    /**
     * インデント
     */
    indent: {
      type: Number,
      default: 0,
    },
    /**
     * ユーザー数表示フラグ
     */
    showUsersCount: {
      type: Boolean,
      default: true,
    },
    /**
     * ユーザー数表示フラグ
     */
    showTagUsersCount: {
      type: Boolean,
      default: true,
    },
  },
  emits: [
    /**
     * アイテム選択イベント
     * @param {Object} data 選択情報({ key: id })
     */
    'indoor-item-selected',
    /**
     * ビル名称クリックイベント
     * @param {Number} id ビルID
     */
    'indoor-building-name-click',
  ],
  data: function () {
    return {
      // 子ノードの表示状態
      isChildNodeShown: false,
    }
  },
  computed: {
    /**
     * 含まれる屋内レベルIDのSet
     * @type {Set}
     */
    indoorLevelIdSet: function () {
      return new Set(this.indoorLevelNodes.map((level) => level.id))
    },
    /**
     * ユーザー数
     * @type {Number}
     */
    usersCount: function () {
      return this.users.filter(
        (user) =>
          this.indoorLevelIdSet.has(user.indoor_level_id) &&
          user.role_id !== TAG_USER_ROLE_ID
      ).length
    },
    /**
     * タグユーザー数
     * @type {Number}
     */
    tagUsersCount: function () {
      return this.users.filter(
        (user) =>
          this.indoorLevelIdSet.has(user.indoor_level_id) &&
          user.role_id === TAG_USER_ROLE_ID
      ).length
    },
    /**
     * 選択されている場合はtrue
     * @type {Boolean}
     */
    isSelected: function () {
      return this.selectedId.indoorBuilding === this.id
    },
    /**
     * 対象になっている子ノード数
     * @type {Number}
     */
    childTargetCount: function () {
      return this.indoorLevelNodes.filter((node) => node.isTarget).length
    },
  },
  watch: {
    isTarget: {
      immediate: true,
      handler: function (value) {
        if (value === false) {
          this.isChildNodeShown = false
        } else {
          this.isChildNodeShown = this.isSelected
        }
      },
    },
    isSelected: function (value) {
      if (value) {
        this.isChildNodeShown = true
      }
    },
  },
  methods: {
    /**
     * 矢印クリック時処理
     */
    $_onArrowClick: function () {
      this.isChildNodeShown = !this.isChildNodeShown
    },

    /**
     * アイテムクリック時処理
     */
    $_onItemClick: function () {
      const value = this.isSelected ? null : this.id
      this.$_emitSelectedEvent({ indoorBuilding: value })
    },

    /**
     * 子アイテム選択時処理
     * @param {Object} data 選択情報({ key: id })
     */
    $_onChildItemSelected: function (data) {
      let result
      const isChildSelected = data && data.indoorLevel
      if (isChildSelected) {
        // 自身も選択されたことにする
        result = { ...data, indoorBuilding: this.id }
      } else {
        // フロアの選択が解除された場合は自身の選択も解除する
        result = { ...data, indoorBuilding: null }
      }
      this.$_emitSelectedEvent(result)
    },

    /**
     * 選択イベント発行
     * @param {Object} data 選択情報({ key: id })
     */
    $_emitSelectedEvent: function (data) {
      this.$emit('indoor-item-selected', data)
    },

    /**
     * アイテム名クリック時処理
     */
    $_onItemNameClick: function () {
      this.$emit('indoor-building-name-click', this.id)
    },
  },
}
</script>

<style lang="scss" scoped>
$-row-height: 56px;

.member-indoor-item-building-node {
  width: 100%;
}

.node-contents {
  --padding-left-add: 0;
  width: 100%;
  min-height: $-row-height;
  padding: 0 8px 0 calc(8px + var(--padding-left-add));
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eaecef;
  user-select: none;
  cursor: pointer;

  &.selected {
    background-color: #f7f7f7;
  }
}

.item-label {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 16px;
}

.arrow-wrapper {
  width: $-row-height;
  height: $-row-height;
  margin: 0 -16px 0 -4px;

  .arrow {
    width: $-row-height;
    height: $-row-height;
    fill: none;
    stroke: rgba(0, 0, 0, 0.5);
    stroke-width: 0.75;
    transform: rotate(-90deg);
    @include mixin-animation(0.1s);

    &:hover {
      stroke: $text-color;
      stroke-width: 1;
    }

    &.is-open {
      transform: rotate(0deg);
    }
  }
}

.icon-wrapper {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background-color: #e6e6e6;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;

  .building-icon {
    fill: $text-color;
  }
}

.item-name {
  color: $text-color-link;
  font-weight: $font-weight-bold;
  text-align: left;
  margin: 4px 0;

  &:hover {
    text-decoration: underline;
  }
}

.counts {
  color: $text-color-weak;
  height: 100%;
  font-size: $font-size-small;
  text-align: right;
  margin: 0 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  row-gap: 4px;

  .row {
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    column-gap: 8px;
    flex-wrap: nowrap;
    white-space: nowrap;
  }

  .user-icon {
    width: 12px;
    height: 12px;
    fill: #555555;
  }
}

.children-list {
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
}
</style>
